// Import category images from assets
import prostheticsImage from 'assets/images/prosthetics.jpg';
import rotaryCuttersImage from 'assets/images/rotary-cutters.jpg';
import cobaltChromeImage from 'assets/images/cobalt-chrome.jpg';
import crownBridgesImage from 'assets/images/crown-bridges.jpg';
import orthodonticsImage from 'assets/images/orthodontics.jpg';
import castingWaxesImage from 'assets/images/casting-waxes.jpg';
import handToolsImage from 'assets/images/hand-tools.jpg';
import mouthguardMaterialsImage from 'assets/images/mouthguard-materials.jpg';

// Navigation constants
export interface NavigationItem {
  label: string;
  path: string;
  /** Rendered as a dropdown; the parent label still links to `path`. */
  children?: ReadonlyArray<{ label: string; path: string }>;
}

// Shop and Special Offer stay top-level; the content/brand pages are nested
// under About Us to keep the bar short enough for the account credit summary.
export const NAVIGATION_ITEMS: ReadonlyArray<NavigationItem> = [
  {
    label: 'Shop',
    path: '/shop',
  },
  {
    label: 'Special Offer',
    path: '/special-offer',
  },
  {
    label: 'About Us',
    path: '/about',
    children: [
      {
        label: 'Our Vision',
        path: '/mission-vision-values',
      },
      {
        label: 'Blog',
        path: '/blog',
      },
      {
        label: 'Resources',
        path: '/resources',
      },
    ],
  },
];

// Route constants
export const ROUTES = {
  HOME: '/',
  SHOP: '/shop',
  PRODUCT: '/product',
  ABOUT: '/about',
  BLOG: '/blog',
  RESOURCES: '/resources',
  CONTACT: '/contact',
  LOGIN: '/login',
  REGISTER: '/register',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  CART: '/cart',
  CHECKOUT: '/checkout',
  ACCOUNT: '/account',
  SPECIAL_OFFER: '/special-offer',
  PRIVACY_POLICY: '/privacy-policy',
  TERMS_AND_CONDITIONS: '/terms-and-conditions',
  RETURNS_POLICY: '/returns-policy',
  COOKIES_POLICY: '/cookies-policy',
  MISSION: '/mission-vision-values',
} as const;

export const SOCIAL_MEDIA_LINKS = {
  FACEBOOK: 'https://www.facebook.com/people/Abbey-Dental-Supplies/100093780965061/',
  INSTAGRAM: 'https://www.instagram.com/abbey_dental_supplies',
  LINKEDIN: 'https://www.linkedin.com/company/abbey-dental-supplies/',
} as const;

// API configuration
export const API_BASE_URL: string = process.env.REACT_APP_API_URL || '';

// API endpoints
export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: '/api/v1/customers/login',
    REGISTER: '/api/v1/customers/register',
    LOGOUT: '/auth/logout',
    REFRESH: '/api/v1/customers/refresh-token',
  },
  PRODUCTS: {
    LIST: '/products',
    DETAIL: '/products/:id',
    SEARCH: '/products/search',
    CATEGORIES: '/products/categories',
  },
  CART: {
    GET: '/cart',
    ADD_ITEM: '/cart/items',
    UPDATE_ITEM: '/cart/items/:id',
    REMOVE_ITEM: '/cart/items/:id',
    CLEAR: '/cart/clear',
  },
  ORDERS: {
    LIST: '/orders',
    CREATE: '/orders',
    DETAIL: '/orders/:id',
  },
  CONTACT: {
    SUBMIT: '/contact',
  },
  NEWSLETTER: {
    SUBSCRIBE: '/newsletter/subscribe',
  },
} as const;

// Product categories (based on the design)
export const PRODUCT_CATEGORIES = [
  {
    id: 'prosthetics',
    name: 'Prosthetics',
    slug: 'prosthetics',
    image: prostheticsImage,
    description: 'Dental prosthetics and implants',
  },
  {
    id: 'rotary-cutters',
    name: 'Rotary Cutters',
    slug: 'rotary',
    image: rotaryCuttersImage,
    description: 'Dental rotary cutters and burs',
  },
  {
    id: 'cobalt-chrome',
    name: 'Cobalt Chrome',
    slug: 'cobalt-chrome',
    image: cobaltChromeImage,
    description: 'Cobalt chrome dental materials',
  },
  {
    id: 'crown-bridge',
    name: 'Crown & Bridge',
    slug: 'crown-bridge',
    image: crownBridgesImage,
    description: 'Crown and bridge materials',
  },
  {
    id: 'orthodontics',
    name: 'Orthodontics',
    slug: 'orthodontics',
    image: orthodonticsImage,
    description: 'Orthodontic supplies and materials',
  },
  {
    id: 'casting-waxes',
    name: 'Casting Waxes',
    slug: 'waxes',
    image: castingWaxesImage,
    description: 'Dental casting waxes',
  },
  {
    id: 'tools',
    name: 'Tools',
    slug: 'hand-tools',
    image: handToolsImage,
    description: 'Dental tools and instruments',
  },
  {
    id: 'mouthguard-materials',
    name: 'Mouthguard Materials',
    slug: 'mouthguard-materials',
    image: mouthguardMaterialsImage,
    description: 'Mouthguard materials and supplies',
  },
] as const;

// Company information
export const COMPANY_INFO = {
  name: 'Abbey Dental Supplies',
  email: 'sales@abbeydentalsupplies.com',
  phone: '0208 683 4528',
  address: {
    street: '',
    city: 'London',
    state: '',
    zipCode: '',
    country: 'United Kingdom',
  },
} as const;

// SEO defaults
export const SEO_DEFAULTS = {
  title: 'Abbey Dental Supplies - Trusted Dental Supplies for Every Professional',
  description: 'Explore a wide range of high-quality dental products trusted by clinics, laboratories, and professionals. From cutting-edge digital equipment to essential dental tools, we\'ve got you covered.',
  keywords: ['dental supplies', 'dental equipment', 'dental tools', 'dental lab', 'dental prosthetics', 'dental materials'],
  image: '/images/og-image.png',
  url: 'https://abbeydentalsupplies.com',
} as const;

// Breakpoints for responsive design
export const BREAKPOINTS = {
  xs: 0,
  sm: 600,
  md: 960,
  lg: 1280,
  xl: 1920,
} as const;

// Pagination defaults
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 12,
  MAX_PAGE_SIZE: 50,
} as const;

// Local storage keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'auth_token',
  USER_DATA: 'user_data',
  CART_DATA: 'cart_data',
  THEME: 'theme',
  LANGUAGE: 'language',
  ADMIN_AUTH_TOKEN: 'admin_access_token',
  ADMIN_USER: 'admin_user',
} as const;

// Currency options
export const CURRENCIES = [
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
] as const;

// Error messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error. Please check your connection and try again.',
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  NOT_FOUND: 'The requested resource was not found.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  SERVER_ERROR: 'An unexpected error occurred. Please try again later.',
} as const;

// Success messages
export const SUCCESS_MESSAGES = {
  LOGIN_SUCCESS: 'Successfully logged in.',
  REGISTER_SUCCESS: 'Account created successfully.',
  LOGOUT_SUCCESS: 'Successfully logged out.',
  CART_ITEM_ADDED: 'Item added to cart successfully.',
  CART_ITEM_REMOVED: 'Item removed from cart successfully.',
  ORDER_PLACED: 'Order placed successfully.',
  CONTACT_SUBMITTED: 'Message sent successfully. We\'ll get back to you soon.',
  NEWSLETTER_SUBSCRIBED: 'Successfully subscribed to newsletter.',
} as const; 